<template>
  <ax-dialog 
    ref="dialog"
    title="Configure Techniques" 
    linkText="Configure Techniques"
    color="primary"
    size="medium" 
    persistent block
    @submitted="onSubmit"
  >
    <template slot="content">
      <v-tabs v-model="tab" fixed-tabs>
        <v-tab key="mmh">MMH</v-tab>
        <v-tab key="auto">Auto</v-tab>
      </v-tabs>
      <v-divider />
      <v-tabs-items v-model="tab">
        <v-tab-item key="mmh">
          <v-card-text>
            <v-row>
              <v-col><v-text-field  :label="mmh.iterations.title" v-model.number="settings.mmh.iterations" :hint="mmh.iterations.hint" persistent-hint /></v-col>
              <v-col><v-text-field  :label="mmh.threshold.title" v-model.number="settings.mmh.threshold" :hint="mmh.threshold.hint" persistent-hint /></v-col>
              <v-col><v-text-field  :label="mmh.minimum.title" v-model.number="settings.mmh.minimum" :hint="mmh.minimum.hint" persistent-hint /></v-col>
            </v-row>

            <v-checkbox :label="mmh.absolute.title" v-model="settings.mmh.absolute" />
            <v-text-field   :label="mmh.drop_threshold.title" v-model.number="settings.mmh.drop_threshold" hide-details />

            <v-checkbox :label="mmh.reanchor.title" v-model="settings.mmh.reanchor" />
            <v-row>
              <v-col><v-text-field :disabled="!settings.mmh.reanchor"  :label="mmh.reanchor_min_months.title" :hint="mmh.reanchor_min_months.hint" v-model.number="settings.mmh.reanchor_min_months"  persistent-hint /></v-col>
              <v-col><v-text-field :disabled="!settings.mmh.reanchor"  :label="mmh.reanchor_min_peak_fraction.title" v-model.number="settings.mmh.reanchor_min_peak_fraction"  persistent-hint /></v-col>
            </v-row>

            <v-row class="align-center me-0">
              <v-checkbox v-model="settings.mmh.reanchor_trim_tail" hide-details class="shrink mx-3 mt-0 mb-3"></v-checkbox>
              <v-text-field :disabled="!settings.mmh.reanchor_trim_tail" :label="mmh.reanchor_trim_tail_months.title" suffix="months" :hint="mmh.reanchor_trim_tail.hint" persistent-hint v-model.number="settings.mmh.reanchor_trim_tail_months" />
            </v-row>
            
            <v-row class="align-center">
              <v-checkbox v-model="settings.mmh.align_fit" hide-details :label="mmh.align_fit.title" class="shrink mx-3 mt-0 mb-3"></v-checkbox>
              <v-text-field :disabled="!settings.mmh.align_fit" :label="mmh.align_window_months.title" v-model.number="settings.mmh.align_window_months" />
            </v-row>

          </v-card-text>
        </v-tab-item>
        <v-tab-item key="auto">
          <v-card-text>
            <v-range-slider class="mt-8" :label="auto.Q.title" :min="auto.Q.min" :max="auto.Q.max" :step="auto.Q.step" :hint="rangeHint(settings.auto.Q)" v-model="settings.auto.Q" persistent-hint>
              <template v-slot:thumb-label="props">
                {{ props.value }}
              </template>
            </v-range-slider>
            <v-range-slider :label="auto.D.title" :min="auto.D.min" :max="auto.D.max" :step="auto.D.step" :hint="rangeHint(settings.auto.D)" v-model="settings.auto.D" persistent-hint>
              <template v-slot:thumb-label="props">
                {{ props.value }}
              </template>
            </v-range-slider>
            <v-range-slider :label="auto.B.title" :min="auto.B.min" :max="auto.B.max" :step="auto.B.step" :hint="rangeHint(settings.auto.B)" v-model="settings.auto.B" persistent-hint>
              <template v-slot:thumb-label="props">
                {{ props.value }}
              </template>
            </v-range-slider>
          </v-card-text>
        </v-tab-item>
      </v-tabs-items>
    </template>
  </ax-dialog>
</template>
<script>
import AxDialog from '@/components/dialog'
export default {
  name: 'dlgTechniques',
  components: { AxDialog },
  props: {
    // The prediction's own config.defaults, if any were already saved
    // (e.g. editing an existing prediction). Merged over this dialog's
    // own hardcoded defaults on open, so reopening it reflects what was
    // actually saved instead of always resetting to the baseline.
    initial: { type: Object, default: () => ({}) }
  },
  data () {
    return {
      tab: null,
      settings: { 
        auto: { 
          version: 'advanced', 
          Q: [ 0.0, 1.0 ], 
          D: [ 0.0, 2.0 ], 
          B: [ 0.0, 1.5 ] 
        }, 
        mmh: { 
          version: 'original',
          iterations: 3,
          threshold: 0.15,
          minimum: 0.65, 
          absolute: true, 
          drop_threshold: 60, 
          reanchor: true, 
          reanchor_min_months: 48, 
          reanchor_min_peak_fraction: 0.1, 
          reanchor_trim_tail: true, 
          reanchor_trim_tail_months: 12, 
          align_fit: true, 
          align_window_months: 3 
        } 
      }, 
      mmh: {
        version: { title: 'Version of algorithm to use', default: 'original', choices: ['original'] },
        iterations: { title: 'Iterations', hint: '# of tries to remove outliers', default: 0 },
        threshold: { title: 'Threshold', hint: 'Threshold to qualify as outlier', default: 0 },
        minimum: { title: 'Minimum', hint: 'Retain this much original data', default: 0 },
        absolute: { title: 'Use absolute values for Arps parameters', default: false },
        drop_threshold: { title: 'Ignore data less than this volume', default: 0 },
        reanchor: { title: 'Match decline to most recent peak', default: false },
        reanchor_min_months: { title: 'Reanchor Months', hint: 'Minimum number of months to qualify for reanchor', default: 0 },
        reanchor_min_peak_fraction: { title: 'Size of peaks to detect', default: 0 },
        reanchor_trim_tail: { title: '', hint: 'Filter influence of unrealistic predictions by choosing the number of months that influcence', default: false },
        reanchor_trim_tail_months: { title: '# of months to cut', hint: 'Number of unrealistic prediction months to cut', default: 0 },
        align_fit: { title: 'Align prediction', hint: 'Align fit with prediction', default: false },
        align_window_months: { title: '# of reference months', default: 0 },
      },
      auto: {
        version: { title: 'Version of algorithm to use', default: 'original', choices: ['original','advanced'] },
        Q: { title: 'Fit qi between ', default: [0,0], min: 0, max: 1, step: 0.1 },
        D: { title: 'Fit Di between', default: [0,0], min: 0, max: 2, step: 0.1 },
        B: { title: 'Fit b between', default: [0,0], min: 0, max: 1.5, step: 0.1 },
      }
    }
  },
  watch: {
    // created() runs when this component is created, which happens before
    // Prediction.vue's own mounted() replaces its placeholder config with
    // the real saved prediction (child components mount before the
    // parent's mounted() hook fires). A watcher re-applies the merge once
    // the real value actually arrives, instead of only ever seeing {}.
    initial: {
      immediate: true,
      deep: true,
      handler(val) {
        if (val && val.mmh) {
          Object.assign(this.settings.mmh, val.mmh)
        }
        if (val && val.auto) {
          Object.assign(this.settings.auto, val.auto)
        }
      }
    }
  },
  methods: {
    onSubmit() {
      this.$emit('submitted', this.settings)
      this.$refs.dialog.show = false
    },
    rangeHint(i) {
      return `[${i[0]}, ${i[1]}]`
    }
  }
}
</script>
